import { useMemo } from 'react'

import { useHistory } from 'react-router-dom'

import { useSearchBar } from '@centivo/provider-directory-components'
import type {
  SearchParams as DirectorySearchBarParams,
  UseSearchBarResult,
} from '@centivo/provider-directory-components'

import { getProviderDomainUrlForContext } from 'src/context/functions/getAppConfig'

import {
  createConditionsFetcher,
  createLocationServiceAdapter,
  createServiceLinesFetcher,
  createSmartSuggestionFetcher,
} from './searchBarAdapters'
import { encodeResultsSearch } from './resultsSearchParams'

export interface DirectorySearchBarInitial {
  query?: string
  zipCode?: string
  placeId?: string
}

/**
 * Wires the library `SearchBar` to the directory search adapters and navigates
 * to `/results` on submit. Shared by the global header search and the home
 * page search so both stay in sync.
 *
 * `onNavigate` (optional) is invoked with the exact `location.search` string
 * this bar is about to push. The header search uses it to tell its own submits
 * apart from external URL changes so it can avoid remounting/reseeding itself.
 */
export const useDirectorySearchBar = (
  initial?: DirectorySearchBarInitial,
  onNavigate?: (search: string) => void
): UseSearchBarResult => {
  const history = useHistory()
  const providerApiUrl = getProviderDomainUrlForContext()

  const locationService = useMemo(() => createLocationServiceAdapter(), [])
  const fetchServiceLines = useMemo(
    () => createServiceLinesFetcher(providerApiUrl),
    [providerApiUrl]
  )
  const fetchConditions = useMemo(
    () => createConditionsFetcher(providerApiUrl),
    [providerApiUrl]
  )
  const fetchSmartSuggestion = useMemo(() => createSmartSuggestionFetcher(), [])

  const onSearch = (params: DirectorySearchBarParams) => {
    const search = `?${encodeResultsSearch(params)}`
    onNavigate?.(search)
    history.push({ pathname: '/results', search })
  }

  return useSearchBar(onSearch, {
    initialQuery: initial?.query ?? '',
    initialZipCode: initial?.zipCode ?? '',
    initialPlaceId: initial?.placeId,
    locationService,
    geolocation: false,
    fetchServiceLines,
    fetchConditions,
    fetchSmartSuggestion,
  })
}
